import { useEffect, useRef, useState } from 'react';

export interface AsyncState<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  reload: () => void;
}

interface Settled<T> {
  key: string;
  data: T | null;
  error: Error | null;
}

/**
 * The one data-fetching primitive the pages share: run an async producer,
 * expose data/error/loading, ignore results that arrive for a stale key.
 * Loading is *derived* — the settled result either matches the current key or
 * it does not — so nothing ever sets state synchronously in an effect.
 * Deliberately not a cache: this dashboard's reads are cheap and correctness
 * beats staleness handling.
 */
export function useAsync<T>(producer: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [settled, setSettled] = useState<Settled<T> | null>(null);
  const [retry, setRetry] = useState(0);

  // The producer is intentionally not a dependency — callers pass inline
  // closures; what identifies a fetch is the caller's dependency list.
  const producerRef = useRef(producer);
  useEffect(() => {
    producerRef.current = producer;
  });

  const key = JSON.stringify([deps, retry]);

  useEffect(() => {
    let cancelled = false;
    producerRef.current().then(
      (data) => {
        if (!cancelled) setSettled({ key, data, error: null });
      },
      (cause: unknown) => {
        if (!cancelled) {
          setSettled({
            key,
            data: null,
            error: cause instanceof Error ? cause : new Error(String(cause)),
          });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [key]);

  const current = settled?.key === key ? settled : null;
  return {
    data: current?.data ?? null,
    error: current?.error ?? null,
    loading: current === null,
    reload: () => setRetry((tick) => tick + 1),
  };
}
